/**
 * Phase H — Discovery scan context.
 *
 * <p>Surfaces a {@code triggerScan()} function and the current
 * {@code runId} state to any descendant of the Discovery Hub. Owns:
 *   • Calling {@code shadowDiscoveryScanTriggerAsync} on demand.
 *   • Holding the most recent runId.
 *   • Hosting the {@link ScanProgressDrawer} so it persists across
 *     tab navigation within the hub (a tab switch should not abort
 *     a running scan or close the drawer).
 *
 * <p>Why context, not prop drilling: the trigger button lives inside
 * {@code ShadowDiscoveryPageV2}, which is mounted as an embedded tab
 * under the hub. Passing the trigger callback through V2's prop
 * surface would require touching the V2 component everywhere — and V2
 * is also used standalone on {@code /discovery-classic}. The
 * {@code useDiscoveryScanContext()} hook returns {@code null} when
 * V2 is rendered outside the provider, so V2 can fall back to the
 * legacy sync trigger without breaking the classic page.
 */
import React, { createContext, useCallback, useContext, useState } from 'react';
import * as api from '../../services/api';
import ScanProgressDrawer from './ScanProgressDrawer';

const DiscoveryScanContext = createContext(null);

export function useDiscoveryScanContext() {
  return useContext(DiscoveryScanContext);
}

export function DiscoveryScanProvider({ children, onScanCompleted }) {
  const [runId, setRunId] = useState(null);
  /* Separate from runId on purpose. runId says "a drawer is mounted"; running says
   * "a scan is in flight". They were the same thing until the drawer learned to
   * collapse itself to a pill five seconds after a clean finish — after which runId
   * stays set, and anything derived from it (the Run scan button) stayed disabled
   * reading "Scan running…" beside a pill saying the scan was complete. */
  const [running, setRunning] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [lastError, setLastError] = useState(null);

  /* DISC-IMPR-B — Accept an optional {engineTypes:[...]} filter.
   * Calling triggerScan() with no args or an empty list runs all six
   * engines (back-compat with every existing call site). Passing a
   * subset runs only those engines — the BE filterEngines() drops
   * unknowns and falls back to ALL if the resulting list is empty,
   * so the contract is safe either way.
   *
   * Phase 2 (task #93) — also accept {enginePicks:[...]} where each pick
   * is {engineType, ruleIds:[uuid…], targets:[string…]}. When supplied,
   * the BE routes through DiscoveryEngineOrchestrator.runFullScanAsync
   * (picks) which narrows rules per pick + transiently overrides
   * ruleConfig.targets. enginePicks takes precedence over engineTypes
   * if both are present. */
  const triggerScan = useCallback(async ({ engineTypes, enginePicks } = {}) => {
    setTriggering(true);
    setLastError(null);
    try {
      let body;
      if (Array.isArray(enginePicks) && enginePicks.length > 0) {
        body = { enginePicks };
      } else if (Array.isArray(engineTypes) && engineTypes.length > 0) {
        body = { engineTypes };
      } else {
        body = {};
      }
      const r = await api.shadowDiscoveryScanTriggerAsync(body);
      // apiFetch always returns { ok, data, etag } — surface the
      // backend error message verbatim instead of swallowing it as a
      // generic "no runId" failure so the operator sees the real cause
      // (RBAC denial, validation error, etc.).
      if (r && r.ok === false) {
        throw new Error(r.error || 'Failed to start scan');
      }
      const newRunId = r?.data?.runId || null;
      if (!newRunId) {
        throw new Error('Backend did not return a runId');
      }
      setRunId(newRunId);
      setRunning(true);
      return newRunId;
    } catch (e) {
      setLastError(e?.message || String(e));
      throw e;
    } finally {
      setTriggering(false);
    }
  }, []);

  const closeDrawer = useCallback(() => {
    setRunId(null);
    setRunning(false);
  }, []);

  const handleScanCompleted = useCallback((run) => {
    /* The drawer calls this on COMPLETED and on FAILED, so a failed run releases the
     * button too. The drawer itself stays mounted on runId so its completion pill
     * survives for the operator to read. */
    setRunning(false);
    if (typeof onScanCompleted === 'function') {
      try { onScanCompleted(run); } catch { /* never block the drawer on a parent handler */ }
    }
  }, [onScanCompleted]);

  const value = {
    runId,
    running,
    triggering,
    lastError,
    triggerScan,
    closeDrawer,
  };

  return (
    <DiscoveryScanContext.Provider value={value}>
      {children}
      <ScanProgressDrawer
        runId={runId}
        onClose={closeDrawer}
        onComplete={handleScanCompleted}
      />
    </DiscoveryScanContext.Provider>
  );
}
